/**
 * EducationPage -- CRUD for education entries in the content library.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { Panel } from '../../components/ui/Panel';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { MonthYearPicker } from '../../components/ui/MonthYearPicker';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { useSaveHandler } from '../../hooks/useSaveHandler';
import { logger } from '../../services/logger.client';
import type { Education } from '../../types/cms.types';

const log = logger.child({ module: 'EducationPage' });

/* ------------------------------------------------------------------ */
/*  Education Editor                                                  */
/* ------------------------------------------------------------------ */

interface EditorProps {
  entry: Education;
}

function EducationEditor({ entry }: EditorProps) {
  const { updateEducation, deleteEducation } = useContentActions();

  const [institution, setInstitution] = useState(entry.institution);
  const [degree, setDegree] = useState(entry.degree);
  const [fieldOfStudy, setFieldOfStudy] = useState(entry.field_of_study ?? '');
  const [startDate, setStartDate] = useState(entry.start_date ?? '');
  const [endDate, setEndDate] = useState(entry.end_date ?? '');
  const [description, setDescription] = useState(entry.base_description ?? '');
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useContentStore.getState().error,
  });

  async function handleSave() {
    await executeSave(() =>
      updateEducation(entry.id, {
        institution,
        degree,
        field_of_study: fieldOfStudy || null,
        start_date: startDate || null,
        end_date: endDate || null,
        base_description: description || null,
      }),
    );
  }

  async function handleDelete() {
    if (!window.confirm('Delete this education entry?')) return;
    await deleteEducation(entry.id);
  }

  return (
    <Panel>
      <Panel.Header>
        {entry.institution} - {entry.degree}
      </Panel.Header>
      <Panel.Content>
        <div className="editor-field-group">
          <div className="experience-entry__row">
            <Input
              label="Institution"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
            />
            <Input
              label="Degree"
              value={degree}
              onChange={(e) => setDegree(e.target.value)}
            />
          </div>
          <Input
            label="Field of Study"
            value={fieldOfStudy}
            onChange={(e) => setFieldOfStudy(e.target.value)}
            placeholder="Computer Science"
          />
          <div className="experience-entry__row">
            <MonthYearPicker
              label="Start Date"
              value={startDate}
              onChange={(v) => {
                setStartDate(v);
                if (endDate && v && endDate < v) setEndDate('');
              }}
              placeholder="Select start month"
            />
            <MonthYearPicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              placeholder="Select end month"
              minValue={startDate || undefined}
            />
          </div>
          <Textarea
            label="Description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Notable achievements, GPA, relevant coursework..."
          />
          <div className="cms-page__actions-row">
            {saveError && (
              <span className="editor-page__save-msg editor-page__save-msg--error">
                {saveError}
              </span>
            )}
            {saveMessage && (
              <span className="editor-page__save-msg">{saveMessage}</span>
            )}
            <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
              Delete
            </Button>
            <Button variant="primary" size="sm" onClick={() => void handleSave()} loading={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </Panel.Content>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function EducationPage() {
  const education = useContentStore((s) => s.education);
  const isLoading = useContentStore((s) => s.isLoading);
  const error = useContentStore((s) => s.error);
  const { fetchEducation, createEducation } = useContentActions();

  const [showCreate, setShowCreate] = useState(false);
  const [newInstitution, setNewInstitution] = useState('');
  const [newDegree, setNewDegree] = useState('');

  useEffect(() => {
    log.info('EducationPage mounted');
    void fetchEducation();
  }, [fetchEducation]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newInstitution.trim() || !newDegree.trim()) return;

    await createEducation({
      institution: newInstitution.trim(),
      degree: newDegree.trim(),
      display_order: education.length,
    });

    setNewInstitution('');
    setNewDegree('');
    setShowCreate(false);
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Education</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => setShowCreate(!showCreate)}
        >
          + Add Education
        </Button>
      </header>

      {showCreate && (
        <Panel>
          <Panel.Header>New Education Entry</Panel.Header>
          <Panel.Content>
            <form className="editor-field-group" onSubmit={handleCreate} noValidate>
              <div className="experience-entry__row">
                <Input
                  label="Institution"
                  value={newInstitution}
                  onChange={(e) => setNewInstitution(e.target.value)}
                  placeholder="MIT"
                  required
                />
                <Input
                  label="Degree"
                  value={newDegree}
                  onChange={(e) => setNewDegree(e.target.value)}
                  placeholder="B.S. Computer Science"
                  required
                />
              </div>
              <div className="cms-page__actions-row">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Create
                </Button>
              </div>
            </form>
          </Panel.Content>
        </Panel>
      )}

      <LoadingState loading={isLoading} error={error}>
        {education.length === 0 ? (
          <div className="list-empty">
            No education entries yet. Click '+ Add Education' to get started.
          </div>
        ) : (
          <div className="cms-page__body">
            {education.map((entry) => (
              <EducationEditor key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </LoadingState>
    </div>
  );
}
